import React, { useMemo, useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { FaFolder, FaFolderOpen, FaThumbtack, FaCog } from "react-icons/fa";
import {
  ResourceGroup,
  ResourceType,
  getGroupRefId,
} from "@/models/resource-group";
import { useSidebarContext } from "@/hooks/use-sidebar";
import ResourceGroupContextMenu from "./resource-group-context-menu";
import SidebarFolderHeader, {
  SidebarFolderContent,
  SidebarFolderAction,
} from "./sidebar-folder";

// ============================================================================
// Types
// ============================================================================

/**
 * Generic interface for items that can be organized in resource groups.
 * All items must have an id and a group reference.
 */
export interface GroupableItem {
  id: string;
  group?: { id?: string; _id?: string } | null;
  /** Order within the group */
  order?: number;
}

export interface ResourceGroupItemProps<T extends GroupableItem> {
  /** The resource group to display */
  group: ResourceGroup;
  /** All items of this resource type (to find children by group) */
  allItems: T[];
  /** All groups (to find child groups by parent) */
  allGroups: ResourceGroup[];
  /** Resource type for context */
  resourceType: ResourceType;
  /** Render function for individual items */
  renderItem: (item: T) => React.ReactNode;
  /** Nesting depth for indentation */
  depth?: number;
  // Drag & drop props for adding items to group
  onDragOver?: (e: React.DragEvent, groupId: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, groupId: string) => void;
  isDragOver?: boolean;
  // Drag & drop props for reordering groups
  onGroupDragStart?: (e: React.DragEvent, groupId: string) => void;
  onGroupDragEnd?: () => void;
  isDragging?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ResourceGroupItem component - displays a group of resources in the sidebar.
 * A group contains child items and child groups that can be expanded/collapsed.
 * Supports drag & drop to add items to the group.
 * Supports nested hierarchy via recursive rendering.
 *
 * This is a generic component that works with any resource type (tracker, enrichment, alert).
 */
function ResourceGroupItem<T extends GroupableItem>({
  group,
  allItems,
  allGroups,
  resourceType,
  renderItem,
  depth = 0,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver = false,
  onGroupDragStart,
  onGroupDragEnd,
  isDragging = false,
}: ResourceGroupItemProps<T>): React.ReactElement {
  const { expandedItems, toggleItem, pinItem } = useSidebarContext();

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const isExpanded = expandedItems.includes(group.id!);

  // Find child groups (groups whose parent is this group)
  const childGroups = useMemo(() => {
    return allGroups
      .filter((g) => {
        return getGroupRefId(g.parent) === group.id;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [allGroups, group.id]);

  // Find items that belong to this group (by group reference)
  const childItems = useMemo(() => {
    const filtered = allItems
      .filter((item) => {
        return getGroupRefId(item.group) === group.id;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    return filtered;
  }, [allItems, group.id]);

  // Total children count for badge
  const totalChildrenCount = childGroups.length + childItems.length;

  const handleClick = useCallback(() => {
    toggleItem(group.id!);
  }, [toggleItem, group.id]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handlePin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      pinItem(group.id!, "group", true);
    },
    [pinItem, group.id],
  );

  const handleConfig = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  // Get group color
  const hasCustomColor = !!group.color;
  const groupColor = group.color;

  // Drag & drop handlers
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      onDragOver?.(e, group.id!);
    },
    [onDragOver, group.id],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      onDragLeave?.(e);
    },
    [onDragLeave],
  );

  const handleDropEvent = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onDrop?.(e, group.id!);
    },
    [onDrop, group.id],
  );

  const handleGroupDragStart = useCallback(
    (e: React.DragEvent) => {
      e.stopPropagation();
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", group.id!);
      e.dataTransfer.setData("application/x-group-id", group.id!);
      e.dataTransfer.setData("application/x-resource-type", resourceType);
      onGroupDragStart?.(e, group.id!);
    },
    [onGroupDragStart, group.id, resourceType],
  );

  const handleGroupDragEnd = useCallback(() => {
    onGroupDragEnd?.();
  }, [onGroupDragEnd]);

  // Icon for folder
  const folderIcon = isExpanded ? (
    <FaFolderOpen size={14} />
  ) : (
    <FaFolder size={14} />
  );

  // Hover actions
  const hoverActions = (
    <>
      <SidebarFolderAction
        onClick={handlePin}
        title="Épingler"
        icon={<FaThumbtack size={10} />}
        variant="pin"
      />
      <SidebarFolderAction
        onClick={handleConfig}
        title="Configurer"
        icon={<FaCog size={10} />}
      />
    </>
  );

  return (
    <div
      className={`group/item rounded-lg transition-opacity ${isDragging ? "opacity-50" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvent}
    >
      <SidebarFolderHeader
        name={group.name || "Dossier sans nom"}
        icon={folderIcon}
        count={totalChildrenCount}
        isExpanded={isExpanded}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isDragOver={isDragOver}
        color={hasCustomColor && groupColor ? groupColor : undefined}
        showDragHandle={depth === 0}
        onDragStart={handleGroupDragStart}
        onDragEnd={handleGroupDragEnd}
        hoverActions={hoverActions}
      />

      <SidebarFolderContent
        isExpanded={isExpanded}
        isEmpty={totalChildrenCount === 0}
        emptyMessage="Dossier vide"
        color={hasCustomColor && groupColor ? groupColor : undefined}
      >
        {/* Child Groups (recursive) */}
        {childGroups.map((childGroup) => (
          <ResourceGroupItem<T>
            key={childGroup.id}
            group={childGroup}
            allItems={allItems}
            allGroups={allGroups}
            resourceType={resourceType}
            renderItem={renderItem}
            depth={depth + 1}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            isDragOver={false}
          />
        ))}

        {/* Child Items */}
        {childItems.map((item) => (
          <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
        ))}
      </SidebarFolderContent>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceGroupContextMenu
            position={contextMenu}
            group={group}
            resourceType={resourceType}
            onClose={() => setContextMenu(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

ResourceGroupItem.displayName = "ResourceGroupItem";

export default ResourceGroupItem;
